import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import CompetitionItem from './Components/CompetitionItem';
import { useI18n, useSelector } from '~Hooks';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { Box, Divider } from '~Styles';
import { GetStaffAndCategories, GetItems } from './__queries__';
import Loader from '~ComponentsImproved/Loader';
import { handyBreakpoint } from '~Styles/breakpoints';
import CheckCircle from '~Assets/icons/check-circle.svg';
import LeaderboardContent from './Components/ViewLeaderboard/LeaderboardContent';
import TopBar from './Components/TopBar';
import Tabs, { Tab } from '~ComponentsImproved/Tabs';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  min-height: calc(100vh - 64px);
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  margin-top: 4px;
  padding: 20px 40px;
  box-sizing: border-box;

  ${handyBreakpoint} {
    padding: 16px;
  }
`;

const CompetitionsWrapper = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;

  ${handyBreakpoint} {
    grid-template-columns: 1fr;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const STAFF_AND_CATEGORIES_QUERY = gql`
  query GetStaffAndCategories {
    user {
      id
      company {
        categories {
          id
          nameLang {
            id
            en
            es
            se
            no
          }
        }
      }
    }
  }
`;

const ITEMS_QUERY = gql`
  query GetItems($companyId: String!) {
    items: getItems(companyId: $companyId) {
      count
      results {
        id
        titleLang {
          id
          en
          es
          se
          no
        }
      }
    }
  }
`;

interface Props {
  isHandy?: boolean;
}

const Competitions: FC<Props> = ({ isHandy }) => {
  const { i18n } = useI18n();
  const competitions = useCompetition();

  const staff = useSelector((state) => state.companyStaff)?.staffs ?? [];
  const companyId = useSelector((state) => state.company);

  const [activeTab, setActiveTab] = useState<'active' | 'past'>('active');

  const categoriesQuery = useQuery<GetStaffAndCategories>(STAFF_AND_CATEGORIES_QUERY);
  const itemsQuery = useQuery<GetItems>(ITEMS_QUERY, {
    variables: { companyId },
    fetchPolicy: 'cache-and-network',
  });

  const categories = categoriesQuery.data?.user.company.categories ?? [];
  const items = itemsQuery.data?.items?.results ?? [];

  if (categoriesQuery.loading || competitions.loading || itemsQuery.loading) {
    return (
      <Wrapper>
        {!isHandy && <TopBar />}
        <Box
          style={{ height: '50vh' }}
          display="flex"
          alignItems="center"
          flexDirection="column"
          gap="small"
          justifyContent="center">
          <Loader />
          <Paragraph color="var(--colorWhite)" level={3}>
            {i18n('Competitions.Loading')}
          </Paragraph>
        </Box>
      </Wrapper>
    );
  }

  // No finished or ongoing competitions
  if (!competitions.hasCompetitions) {
    return (
      <Wrapper>
        {/* FOR HANDY: If there is no competitions, we dont even show navigation to it */}
        {!isHandy && <TopBar />}
        <ScrollWrapper>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            padding="xl"
            textAlign="center">
            <Heading level={1} color="var(--colorWhite)" bottom={24}>
              {i18n('Competitions.Intro.Heading')}
            </Heading>
            <Paragraph level={2} color="var(--colorBlueGray200)">
              {i18n('Competitions.Intro.Desc')}
            </Paragraph>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="medium"
              marginTop="medium">
              <Box display="flex" flexDirection="column" textAlign="left" gap="small">
                <Box display="flex" alignItems="center" gap="small">
                  <Icon width="32px" height="32px" src={CheckCircle} color="var(--colorUIGreen)" />
                  <Paragraph level={4} color="var(--colorBlueGray200)">
                    {i18n('Competitions.Intro.Benefit.Leaderboard')}
                  </Paragraph>
                </Box>
                <Box display="flex" alignItems="center" gap="small">
                  <Icon width="32px" height="32px" src={CheckCircle} color="var(--colorUIGreen)" />
                  <Paragraph level={4} color="var(--colorBlueGray200)">
                    {i18n('Competitions.Intro.Benefit.Types')}
                  </Paragraph>
                </Box>
                <Box display="flex" alignItems="center" gap="small">
                  <Icon width="32px" height="32px" src={CheckCircle} color="var(--colorUIGreen)" />
                  <Paragraph level={4} color="var(--colorBlueGray200)">
                    {i18n('Competitions.Intro.Benefit.Fun')}
                  </Paragraph>
                </Box>
              </Box>
            </Box>
          </Box>
        </ScrollWrapper>
      </Wrapper>
    );
  }

  const competitionsToRender =
    activeTab === 'active' ? competitions.onGoing : competitions.finished;

  return (
    <Wrapper>
      {!isHandy && <TopBar />}
      <ScrollWrapper>
        {isHandy && (
          <Heading bottom={16} level={2} color="var(--colorWhite)">
            {i18n('Competitions.Title')}
          </Heading>
        )}

        <Tabs>
          <Tab active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
            {i18n('Competitions.Tabs.Active')}
          </Tab>
          <Tab active={activeTab === 'past'} onClick={() => setActiveTab('past')}>
            {i18n('Competitions.Tabs.Past')}
          </Tab>
        </Tabs>

        <Box display="flex" flexDirection="column" gap="medium">
          {competitionsToRender.length > 0 ? (
            <>
              {competitionsToRender.length === 1 && activeTab === 'active' ? (
                <LeaderboardContent
                  isHandy={isHandy}
                  isFullPage
                  competition={competitionsToRender[0]}
                  categories={categories}
                  items={items}
                />
              ) : (
                <CompetitionsWrapper>
                  {competitionsToRender.map((competition) => (
                    <CompetitionItem
                      key={competition.id}
                      competition={competition}
                      isHandy={isHandy}
                      categories={categories}
                      items={items}
                    />
                  ))}
                </CompetitionsWrapper>
              )}

              {/* We only display the competitions that has an endDate within the last month. This is a text informing the user that. (rest can be viewed in the quick-office) */}
              {activeTab === 'past' ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  textAlign="center"
                  marginTop="medium"
                  style={{
                    maxWidth: '800px',
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingBottom: isHandy ? '24px' : '',
                  }}>
                  <Heading level={isHandy ? 4 : 3} color="var(--colorWhite)" bottom={12}>
                    {i18n('Competitions.Tabs.Past.OlderThanOneMonth')}
                  </Heading>
                  <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)">
                    {i18n('Competitions.Tabs.Past.OlderThanOneMonth.Desc')}
                  </Paragraph>
                </Box>
              ) : (
                <>
                  {competitions.upcoming.length > 0 && (
                    <>
                      <Divider color="var(--colorBlueGray1000)" />
                      <CompetitionsWrapper>
                        {competitions.upcoming.map((competition) => (
                          <CompetitionItem
                            key={competition.id}
                            competition={competition}
                            isHandy={isHandy}
                            categories={categories}
                            items={items}
                          />
                        ))}
                      </CompetitionsWrapper>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            // The company has no created competitions at all
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              marginTop={'xl'}>
              <Heading
                level={isHandy ? 3 : 2}
                color="var(--colorWhite)"
                bottom={isHandy ? 12 : 24}>
                {activeTab === 'active'
                  ? i18n('Competitions.NoCompetitions.Ongoing')
                  : i18n('Competitions.NoCompetitions.Past')}
              </Heading>
              <Paragraph level={isHandy ? 3 : 2} color="var(--colorBlueGray200)">
                {activeTab === 'active'
                  ? i18n('Competitions.NoCompetitions.Ongoing.Desc')
                  : i18n('Competitions.NoCompetitions.Past.Desc')}
              </Paragraph>
            </Box>
          )}
        </Box>
      </ScrollWrapper>
    </Wrapper>
  );
};

export default Competitions;
